import React, { useEffect, useState } from "react";

import { Alert, Badge, Button, ButtonGroup, Checkbox, Icon, Modal } from "../components/ui";
import { REQUIRED_RISKS } from "./waBroadcastApi";

/**
 * Shown when someone asks for a linking code: the risks of linking a number to
 * an unofficial sender, and exactly what Retail Circle keeps. Every statement
 * must be ticked; the backend refuses a code without a recorded acceptance of
 * the current version (WaBroadcastService::TERMS_VERSION), so this is not only
 * a UI gate.
 *
 * Keep the "what we store" list TRUE to the code: group names and sizes, the
 * lists, the send log, and direct replies containing an opt-out word (shown in
 * an alert). Group conversations are never stored. Change the text → bump
 * TERMS_VERSION so everyone accepts the new wording.
 */
const RISKS = [
  {
    key: "third_party",
    label: "This is not an official WhatsApp service",
    text: "Messages go out through Retail Circle's own sender, connected to your WhatsApp as a linked device — the same way WhatsApp Web works. Meta does not approve or support it, and a WhatsApp update can stop it working without notice.",
  },
  {
    key: "ban_risk",
    label: "WhatsApp may restrict or ban the number",
    text: "WhatsApp's terms do not allow automated messaging. Even with the pacing and daily limits built in here, WhatsApp can limit, suspend or permanently ban a number it believes is automated, at any time and without warning.",
  },
  {
    key: "no_reinstatement",
    label: "A banned number may never come back",
    text: "Neither Retail Circle nor anyone else can get a restricted number restored. Losing it can mean losing its chats, groups and contacts on WhatsApp.",
  },
  {
    key: "no_liability",
    label: "Retail Circle is not liable for losses",
    text: "This feature is provided as it is. Retail Circle is not responsible for a restricted or lost number, undelivered messages, or any loss of business that follows.",
  },
  {
    key: "best_practice",
    label: "I will only post where it is welcome",
    text: "I will post only to groups I already belong to that expect trade offers, stay within the daily limits, and stop if members object. I will not use it for spam or unsolicited messages.",
  },
];

export function LinkDisclosure({ open, onClose, onAccept, accepting = false }) {
  const [ticked, setTicked] = useState(() => new Set());
  const [readMessages, setReadMessages] = useState(false);

  useEffect(() => {
    if (open) {
      setTicked(new Set());
      setReadMessages(false);
    }
  }, [open]);

  const all = REQUIRED_RISKS.every((k) => ticked.has(k));
  const tick = (key, on) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="auto"
      size="lg"
      className="wab-sheet wab-disclosure"
      title="Before you link a WhatsApp number"
      description="Read each point and tick it to continue."
      footer={
        <ButtonGroup align="end" stackBelow="sm">
          <Button variant="secondary" size="lg" onClick={onClose}>Cancel</Button>
          <Button
            size="lg"
            disabled={!all}
            loading={accepting}
            onClick={() => onAccept({ accepted: [...ticked], read_messages: readMessages })}
          >
            {all ? "I understand — get the code" : `Tick all ${REQUIRED_RISKS.length} to continue`}
          </Button>
        </ButtonGroup>
      }
    >
      <div className="wab-disclosure__body">
        <Alert tone="error" title="Linking a number carries a real risk of losing it">
          WhatsApp Broadcast works outside WhatsApp's official business tools. Use a number you can afford to lose,
          and never more than the daily limit.
        </Alert>

        <section aria-labelledby="wab-risks-title">
          <h3 id="wab-risks-title" className="wab-subtitle rc-wab-heading">The risks</h3>
          <ul className="wab-disclosure__list">
            {RISKS.map((r) => (
              <li key={r.key}>
                <Checkbox
                  checked={ticked.has(r.key)}
                  onChange={(on) => tick(r.key, on)}
                  label={r.label}
                  description={r.text}
                />
              </li>
            ))}
          </ul>
        </section>

        <section className="wab-privacy" aria-labelledby="wab-privacy-title">
          <h3 id="wab-privacy-title" className="wab-subtitle rc-wab-heading">
            <Icon name="ShieldCheck" size="sm" /> Your data
          </h3>
          <ul className="wab-privacy__list">
            <li>
              <Icon name="Check" size="xs" />
              <span>
                <strong>What we keep:</strong> the number you link, the names and member counts of the groups it is
                already in (so you can build lists), the lists you create, and a log of what was sent to which group
                and whether it arrived.
              </span>
            </li>
            <li>
              <Icon name="Check" size="xs" />
              <span>
                <strong>What we don't:</strong> we do not read, capture or store the messages in your chats or groups.
              </span>
            </li>
            <li>
              <Icon name="Check" size="xs" />
              <span>
                <strong>One exception:</strong> if someone replies directly to this number with an opt-out word such
                as “STOP”, that reply is shown to you in an alert so you can act on it. Other direct messages are
                ignored and not kept.
              </span>
            </li>
            <li>
              <Icon name="Check" size="xs" />
              <span>
                <strong>Unlinking</strong> removes this device and its WhatsApp login from our sender. You can also
                choose to delete the groups, lists and history we hold.
              </span>
            </li>
          </ul>
        </section>

        <section className="wab-optin" aria-label="Optional">
          <Checkbox
            checked={readMessages}
            onChange={(on) => setReadMessages(on)}
            label={
              <span className="wab-optin__label">
                Also analyse my group messages <Badge tone="muted" size="sm">Coming soon</Badge>
              </span>
            }
            description="Optional. When this launches, Retail Circle will read the messages in your groups to find leads for your store and add trade prices to your store's market data. That data will be kept separate for your store and protected. Nothing is read until it launches, and you will be asked to confirm before it starts — ticking this now only records your interest."
          />
        </section>
      </div>
    </Modal>
  );
}

export default LinkDisclosure;
